import { useState, useEffect } from 'react';
import { api } from '@/store';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Package, Plus, Trash2, Loader2, Link2, Edit2, Check, X, RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function SigmaPlansManager({ open, onClose }) {
  const [integrations, setIntegrations] = useState([]);
  const [integId, setIntegId] = useState('');
  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState('');
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ name: '', price: '', months: '1', connections: '1' });
  const [np, setNp] = useState({ name: '', price: '', months: '1', package_id: '', connections: '1', show_in_payment: true });
  const [cachedAt, setCachedAt] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get('/sigma/login/integrations?scope=all').then(r => {
      let list = Array.isArray(r.data) ? r.data : (r.data?.integrations || []);
      list = list.filter(i => i && i.id && (i.provider || 'sigma') === 'sigma');
      setIntegrations(list);
      if (list.length) setIntegId(list[0].id);
    }).catch(() => {});
  }, [open]);

  const load = async (id, refresh = false) => {
    if (!id) return;
    setLoading(true);
    const msgs = refresh
      ? ['Conectando ao painel Sigma…', 'Fazendo login com segurança…', 'Buscando os pacotes…', 'Organizando o catálogo…', 'Quase lá…']
      : ['Carregando…'];
    let mi = 0; setLoadMsg(msgs[0]);
    const timer = setInterval(() => { mi = (mi + 1) % msgs.length; setLoadMsg(msgs[mi]); }, 2200);
    try {
      const [rp, rpl] = await Promise.all([
        api.get(`/sigma/login/${id}/all-packages${refresh ? '?refresh=true' : ''}`),
        api.get(`/sigma/login/${id}/custom-plans`),
      ]);
      setPackages(Array.isArray(rp.data?.packages) ? rp.data.packages : []);
      setPlans(Array.isArray(rpl.data?.plans) ? rpl.data.plans : []);
      setCachedAt(rp.data?.cached_at || null);
    } catch (e) { toast.error('Erro ao carregar pacotes'); }
    finally { clearInterval(timer); setLoading(false); }
  };
  useEffect(() => { if (integId) load(integId); }, [integId]);

  const [criandoTodos, setCriandoTodos] = useState(false);
  const criarTodos = async () => {
    if (!integId) return toast.error('Escolha a integração');
    setCriandoTodos(true);
    try {
      const r = await api.post(`/sigma/login/${integId}/criar-faltantes`);
      toast.success(r.data.mensagem || 'Planos criados!');
      load(integId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setCriandoTodos(false); }
  };
  const createPlan = async () => {
    const price = parseFloat(np.price);
    if (!np.name.trim() || isNaN(price) || price < 0) return toast.error('Preencha nome e preço');
    if (!np.package_id) return toast.error('Escolha o pacote do Sigma');
    try {
      const pkg = packages.find(p => p.package_id === np.package_id);
      await api.post(`/sigma/login/${integId}/custom-plan`, {
        name: np.name.trim(), price, duration_months: parseInt(np.months) || 1,
        package_id: np.package_id, package_name: pkg?.package_name || '',
        connections: parseInt(np.connections) || 1, show_in_payment: np.show_in_payment,
      });
      toast.success('Plano criado!');
      setNp({ ...np, name: '', price: '' });
      load(integId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao criar plano'); }
  };

  const toggleLink = async (plan, val) => {
    try {
      await api.put(`/customer-plans/${plan.id}`, {
        name: plan.name, price: plan.price, duration_months: plan.duration_months,
        description: plan.description || '', active: plan.active !== false,
        sigma_login_integration_id: plan.sigma_login_integration_id,
        sigma_login_package_id: plan.sigma_login_package_id,
        sigma_login_connections: plan.sigma_login_connections || 1,
        show_in_payment: val,
      });
      setPlans(prev => prev.map(x => x.id === plan.id ? { ...x, show_in_payment: val } : x));
    } catch { toast.error('Erro ao atualizar'); }
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setEditData({ name: p.name || '', price: String(p.price ?? ''), months: String(p.duration_months || 1), connections: String(p.sigma_login_connections || 1) });
  };
  const saveEdit = async (p) => {
    const price = parseFloat(editData.price);
    if (!editData.name.trim() || isNaN(price) || price < 0) return toast.error('Preencha nome e preço');
    try {
      await api.put(`/customer-plans/${p.id}`, {
        name: editData.name.trim(), price, duration_months: parseInt(editData.months) || 1,
        description: p.description || '', active: p.active !== false,
        sigma_login_integration_id: p.sigma_login_integration_id,
        sigma_login_package_id: p.sigma_login_package_id,
        sigma_login_connections: parseInt(editData.connections) || 1,
        show_in_payment: p.show_in_payment,
      });
      setPlans(prev => prev.map(x => x.id === p.id ? { ...x, name: editData.name.trim(), price, duration_months: parseInt(editData.months) || 1, sigma_login_connections: parseInt(editData.connections) || 1 } : x));
      setEditId(null);
      toast.success('Plano atualizado');
    } catch { toast.error('Erro ao salvar'); }
  };

  const delPlan = async (id) => {
    if (!window.confirm('Excluir este plano?')) return;
    try { await api.delete(`/customer-plans/${id}`); load(integId); }
    catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível (plano em uso?)'); }
  };

  // agrupa planos por pacote (protegido contra package_id nulo)
  const byPkg = {};
  (plans || []).forEach(p => {
    const key = p.sigma_login_package_id || 'sem-pacote';
    (byPkg[key] = byPkg[key] || []).push(p);
  });
  const pkgName = (id) => (packages.find(p => p.package_id === id)?.package_name) || (id === 'sem-pacote' ? 'Sem pacote' : `Pacote ${id}`);

  const months = [1, 2, 3, 6, 12];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-primary" /> Planos Sigma</DialogTitle>
          <DialogDescription>
            Crie vários planos personalizados vinculados aos pacotes do Sigma. No link aparecem só os marcados "No link".
          </DialogDescription>
        </DialogHeader>

        {/* Integração */}
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-xs">Integração Sigma</Label>
            {cachedAt && !loading && (
              <button onClick={() => load(integId, true)} className="text-[11px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Atualizar pacotes
              </button>
            )}
          </div>
          <Select value={integId || undefined} onValueChange={setIntegId}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Escolha a integração" /></SelectTrigger>
            <SelectContent>
              {integrations.map(i => <SelectItem key={i.id} value={i.id}>{i.name || i.panel_username || 'Integração'}</SelectItem>)}
            </SelectContent>
          </Select>
          {cachedAt && !loading && (
            <p className="text-[10px] text-muted-foreground mt-1">Pacotes atualizados em {new Date(cachedAt).toLocaleString('pt-BR')}</p>
          )}
        </div>

        {loading && (
          <div className="text-center py-4">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-cyan-400 mb-2" />
            <p className="text-sm text-cyan-300">{loadMsg}</p>
          </div>
        )}

        {/* Novo plano */}
        {!loading && (
        <div className="rounded-xl border border-primary/25 bg-primary/[0.04] p-4 space-y-3 mt-2">
          <p className="text-sm font-semibold text-primary flex items-center gap-1"><Plus className="w-4 h-4" /> Novo plano</p>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nome do plano</Label>
              <Input placeholder="Ex.: 1 Tela Mensal Promo" value={np.name} onChange={e => setNp({ ...np, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Link2 className="w-3 h-3" /> Pacote do Sigma</Label>
              <Select value={np.package_id || undefined} onValueChange={v => setNp({ ...np, package_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Escolha o pacote" /></SelectTrigger>
                <SelectContent>
                  {packages.filter(p => p && p.package_id && !p.is_test).map(p => (
                    <SelectItem key={p.package_id} value={p.package_id}>
                      {p.package_name || `Pacote ${p.package_id}`}{p.planos_count ? ` (${p.planos_count})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Duração (meses)</Label>
              <Select value={String(np.months || 1)} onValueChange={v => setNp({ ...np, months: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{months.map(m => <SelectItem key={m} value={String(m)}>{m} {m === 1 ? 'mês' : 'meses'}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Preço (R$)</Label>
                <Input type="number" placeholder="30.00" value={np.price} onChange={e => setNp({ ...np, price: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Telas</Label>
                <Input type="number" min="1" value={np.connections} onChange={e => setNp({ ...np, connections: e.target.value })} className="mt-1" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={np.show_in_payment} onCheckedChange={v => setNp({ ...np, show_in_payment: v })} />
              Mostrar no link
            </label>
            <Button size="sm" onClick={createPlan}><Plus className="w-4 h-4 mr-1" /> Criar</Button>
          </div>
        </div>
        )}

        {/* Botão criar todos os planos faltantes */}
        {!loading && integId && (
          <div className="mt-4 rounded-xl border border-cyan-500/25 bg-cyan-500/[0.06] p-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-foreground">Criar todos os planos do painel</p>
              <p className="text-[11px] text-muted-foreground">Cria de uma vez os pacotes que ainda não têm plano (preço 0, ajuste depois). Os já criados são pulados.</p>
            </div>
            <Button size="sm" onClick={criarTodos} disabled={criandoTodos} className="shrink-0">
              {criandoTodos ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Criar todos
            </Button>
          </div>
        )}

        {/* Planos por pacote */}
        {!loading && (
        <div className="mt-4 space-y-4">
          {Object.keys(byPkg).length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">Nenhum plano criado ainda.</p>
          )}
          {Object.entries(byPkg).map(([pid, arr]) => (
            <div key={pid}>
              <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                <Package className="w-3.5 h-3.5" /> {pkgName(pid)} <span className="text-primary">· {arr.length} plano{arr.length > 1 ? 's' : ''}</span>
              </p>
              <div className="space-y-2">
                {arr.map(p => editId === p.id ? (
                  <div key={p.id} className="rounded-lg border border-primary/40 bg-primary/[0.04] px-3 py-2.5 space-y-2">
                    <Input value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} placeholder="Nome" className="h-8 text-sm" />
                    <div className="grid grid-cols-3 gap-2">
                      <Select value={String(editData.months || 1)} onValueChange={v => setEditData({ ...editData, months: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{months.map(m => <SelectItem key={m} value={String(m)}>{m}m</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="number" value={editData.price} onChange={e => setEditData({ ...editData, price: e.target.value })} className="h-8 text-sm" placeholder="Preço" />
                      <Input type="number" min="1" value={editData.connections} onChange={e => setEditData({ ...editData, connections: e.target.value })} className="h-8 text-sm" placeholder="Telas" />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setEditId(null)}><X className="w-4 h-4 mr-1" />Cancelar</Button>
                      <Button size="sm" onClick={() => saveEdit(p)}><Check className="w-4 h-4 mr-1" />Salvar</Button>
                    </div>
                  </div>
                ) : (
                  <div key={p.id} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2 gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {p.duration_months} {p.duration_months === 1 ? 'mês' : 'meses'} · R$ {Number(p.price || 0).toFixed(2)}
                        {p.sigma_login_connections > 1 ? ` · ${p.sigma_login_connections} telas` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
                        <Switch checked={!!p.show_in_payment} onCheckedChange={v => toggleLink(p, v)} />
                        <span className="hidden sm:inline">No link</span>
                      </label>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(p)}><Edit2 className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-500 h-8 w-8" onClick={() => delPlan(p.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
